import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import ChatWindow from '../components/ChatWindow';
import MessageInput from '../components/MessageInput';
import {
  FiSun,
  FiMoon,
  FiBookOpen,
  FiSettings,
  FiInfo,
  FiLayers,
  FiActivity,
  FiEdit3,
  FiFilm,
  FiClock,
  FiUser,
  FiCalendar
} from 'react-icons/fi';
import { MdAutoAwesome } from 'react-icons/md';
import { BsLightningChargeFill } from 'react-icons/bs';
import { AiOutlineFileText } from 'react-icons/ai';
import { baseUrl } from '../config';

// Camera Reel Component
const CameraReel = ({ side, theme }) => {
  const reelColor = theme === 'dark' ? '#333333' : theme === 'read' ? '#f5e6d3' : '#555555';
  const sprocketColor = theme === 'dark' ? '#111111' : theme === 'read' ? '#d4c5b0' : '#333333';

  return (
    <div className="pb-chatbot-camera-reel">
      <svg className="pb-chatbot-camera-reel-svg" viewBox="0 0 100 1000" preserveAspectRatio="none">
        <rect x="0" y="0" width="100" height="1000" fill={reelColor} />
        {Array.from({ length: 60 }).map((_, i) => (
          <rect
            key={i}
            x={side === "left" ? "60" : "10"}
            y={i * 16 + 8}
            width="30"
            height="8"
            rx="2"
            fill={sprocketColor}
          />
        ))}
        <line x1="0" y1="0" x2="0" y2="1000" stroke="#222222" strokeWidth="2" />
        <line x1="100" y1="0" x2="100" y2="1000" stroke="#222222" strokeWidth="2" />
        <animate
          attributeName="viewBox"
          from="0 0 100 1000"
          to="0 -1000 100 1000"
          dur="20s"
          repeatCount="indefinite"
        />
      </svg>
      <div className="pb-chatbot-camera-gradient-top"></div>
      <div className="pb-chatbot-camera-gradient-bottom"></div>
    </div>
  );
};

// Feature Panel Component
const FeaturePanel = ({
  theme,
  scriptsGeneratedToday,
  maxDailyScripts,
  projectInfo,
  onFeatureClick,
  onScriptBreakdownClick,
  sceneBreakdown,
  conversationHistory,
  currentConversationId,
  onLoadConversation,
  onNewScript
}) => {
  const features = [
    {
      icon: <BsLightningChargeFill className="pb-chatbot-feature-icon" />,
      title: 'Script Analysis',
      color: 'pb-chatbot-color-yellow',
      description: 'AI-powered script analysis'
    },
    {
      icon: <FiFilm className="pb-chatbot-feature-icon" />,
      title: 'Script Breakdown / Storyboard',
      color: 'pb-chatbot-color-indigo',
      description: 'Break down script into scenes'
    },
    {
      icon: <FiEdit3 className="pb-chatbot-feature-icon" />,
      title: 'Documentation',
      color: 'pb-chatbot-color-rose',
      description: 'Edit and refine your scripts'
    },
    {
      icon: <AiOutlineFileText className="pb-chatbot-feature-icon" />,
      title: 'Templates',
      color: 'pb-chatbot-color-blue',
      description: 'Pre-built script formats'
    },
    {
      icon: <FiLayers className="pb-chatbot-feature-icon" />,
      title: 'Version Control',
      color: 'pb-chatbot-color-purple',
      description: 'Track all your changes'
    },
    {
      icon: <FiActivity className="pb-chatbot-feature-icon" />,
      title: 'Real-time Feedback',
      color: 'pb-chatbot-color-green',
      description: 'Instant AI suggestions'
    },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className={`pb-chatbot-feature-panel pb-chatbot-theme-${theme}`}>
      {/* Header */}
      <div className="pb-chatbot-feature-header">
        <div className="pb-chatbot-feature-header-content">
          <MdAutoAwesome className="pb-chatbot-feature-header-icon" />
          <h2 className="pb-chatbot-feature-header-title">Producer Buddy</h2>
        </div>
        <p className="pb-chatbot-feature-header-subtitle">Script Generator Tool</p>
      </div>

      {/* Daily Limit Indicator */}
      <div className="pb-chatbot-daily-limit-card">
        <div className="pb-chatbot-daily-limit-header">
          <span className="pb-chatbot-daily-limit-label">Daily Limit</span>
          <span className={`pb-chatbot-daily-limit-badge ${scriptsGeneratedToday >= maxDailyScripts ? 'pb-chatbot-limit-reached' : 'pb-chatbot-limit-ok'}`}>
            {scriptsGeneratedToday}/{maxDailyScripts} Scripts
          </span>
        </div>
        <div className="pb-chatbot-daily-limit-progress-bg">
          <div
            className={`pb-chatbot-daily-limit-progress-bar ${scriptsGeneratedToday >= maxDailyScripts ? 'pb-chatbot-progress-red' : 'pb-chatbot-progress-teal'}`}
            style={{ width: `${(scriptsGeneratedToday / maxDailyScripts) * 100}%` }}
          ></div>
        </div>
        {scriptsGeneratedToday >= maxDailyScripts && (
          <p className="pb-chatbot-daily-limit-warning">
            ⚠️ Daily limit reached! You can only improve existing scripts.
          </p>
        )}
      </div>

      {/* Scene Breakdown Display */}
      {sceneBreakdown && (
        <div className="pb-chatbot-scene-breakdown-card">
          <h3 className="pb-chatbot-scene-breakdown-title">
            <FiFilm /> Scene Breakdown
          </h3>
          <div className="pb-chatbot-scene-breakdown-content">
            <div className="pb-chatbot-scene-stat">
              <span className="pb-chatbot-scene-label">Total Scenes:</span>
              <span className="pb-chatbot-scene-value">{sceneBreakdown.num_scenes || 0}</span>
            </div>
            <div className="pb-chatbot-scene-stat">
              <span className="pb-chatbot-scene-label">Shooting Days:</span>
              <span className="pb-chatbot-scene-value">{sceneBreakdown.shooting_days || 0}</span>
            </div>
            <div className="pb-chatbot-scene-stat">
              <span className="pb-chatbot-scene-label">VFX Required:</span>
              <span className="pb-chatbot-scene-value">{sceneBreakdown.vfx_required ? 'Yes' : 'No'}</span>
            </div>
            {sceneBreakdown.complexity_score && (
              <div className="pb-chatbot-scene-stat">
                <span className="pb-chatbot-scene-label">Complexity:</span>
                <span className="pb-chatbot-scene-value">{sceneBreakdown.complexity_score}/10</span>
              </div>
            )}
            {sceneBreakdown.characters && sceneBreakdown.characters.length > 0 && (
              <div className="pb-chatbot-scene-list">
                <span className="pb-chatbot-scene-label">Characters ({sceneBreakdown.characters.length}):</span>
                <ul className="pb-chatbot-scene-items">
                  {sceneBreakdown.characters.slice(0, 5).map((char, i) => (
                    <li key={i}>{char.name || char}</li>
                  ))}
                  {sceneBreakdown.characters.length > 5 && (
                    <li>+ {sceneBreakdown.characters.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            {sceneBreakdown.locations && sceneBreakdown.locations.length > 0 && (
              <div className="pb-chatbot-scene-list">
                <span className="pb-chatbot-scene-label">Locations ({sceneBreakdown.locations.length}):</span>
                <ul className="pb-chatbot-scene-items">
                  {sceneBreakdown.locations.slice(0, 5).map((loc, i) => (
                    <li key={i}>{loc}</li>
                  ))}
                  {sceneBreakdown.locations.length > 5 && (
                    <li>+ {sceneBreakdown.locations.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Project Context Info */}
      {projectInfo && (
        <div className="pb-chatbot-project-info-card">
          <h3 className="pb-chatbot-project-info-title">
            <FiInfo /> Current Project
          </h3>
          <div className="pb-chatbot-project-info-content">
            {projectInfo.name && (
              <div className="pb-chatbot-project-detail">
                <FiFilm className="pb-chatbot-project-detail-icon" />
                <div>
                  <span className="pb-chatbot-project-label">Project Name:</span>
                  <p className="pb-chatbot-project-value">{projectInfo.name}</p>
                </div>
              </div>
            )}
            {projectInfo.description && (
              <div className="pb-chatbot-project-detail">
                <AiOutlineFileText className="pb-chatbot-project-detail-icon" />
                <div>
                  <span className="pb-chatbot-project-label">Description:</span>
                  <p className="pb-chatbot-project-value">{projectInfo.description}</p>
                </div>
              </div>
            )}
            <div className="pb-chatbot-project-detail">
              <FiLayers className="pb-chatbot-project-detail-icon" />
              <div>
                <span className="pb-chatbot-project-label">Genre:</span>
                <p className="pb-chatbot-project-value">{projectInfo.genre || 'Not set'}</p>
              </div>
            </div>
            <div className="pb-chatbot-project-detail">
              <FiFilm className="pb-chatbot-project-detail-icon" />
              <div>
                <span className="pb-chatbot-project-label">Type:</span>
                <p className="pb-chatbot-project-value">{projectInfo.type || 'Not set'}</p>
              </div>
            </div>
            {projectInfo.created_at && (
              <div className="pb-chatbot-project-detail">
                <FiCalendar className="pb-chatbot-project-detail-icon" />
                <div>
                  <span className="pb-chatbot-project-label">Created:</span>
                  <p className="pb-chatbot-project-value">{new Date(projectInfo.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            )}
            <p className="pb-chatbot-project-lock-notice">
              🔒 Scripts are locked to this project context
            </p>
          </div>
        </div>
      )}

      {/* Conversation History */}
      {conversationHistory && conversationHistory.length > 0 && (
        <div className="pb-chatbot-conversation-history-card">
          <div className="pb-chatbot-conversation-history-header">
            <h3 className="pb-chatbot-conversation-history-title">
              <FiClock /> Past Scripts ({conversationHistory.length})
            </h3>
          </div>
          <div className="pb-chatbot-conversation-list">
            {conversationHistory.map((conv, i) => (
              <div
                key={conv.id}
                className={`pb-chatbot-conversation-item ${currentConversationId === conv.id ? 'pb-chatbot-conversation-active' : ''}`}
                onClick={() => onLoadConversation(conv.id)}
              >
                <div className="pb-chatbot-conversation-item-header">
                  <span className="pb-chatbot-conversation-name">{conv.title}</span>
                  {currentConversationId === conv.id && (
                    <span className="pb-chatbot-conversation-active-badge">Active</span>
                  )}
                </div>
                <div className="pb-chatbot-conversation-date">
                  <FiCalendar className="pb-chatbot-conversation-date-icon" />
                  {formatDate(conv.updated_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features Section */}
      <div className="pb-chatbot-features-section">
        <h3 className="pb-chatbot-features-title">
          <FiSettings className="pb-chatbot-features-title-icon" /> Features
        </h3>
        <div className="pb-chatbot-features-list">
          {features.map((feature, index) => (
            <div
              key={index}
              className="pb-chatbot-feature-card"
              onClick={() => {
                if (feature.title === 'Documentation') {
                  onFeatureClick();
                } else if (feature.title === 'Script Breakdown / Storyboard') {
                  onScriptBreakdownClick();
                }
              }}
              style={{
                cursor: (feature.title === 'Documentation' || feature.title === 'Script Breakdown / Storyboard')
                  ? 'pointer'
                  : 'default'
              }}
            >
              <div className="pb-chatbot-feature-card-content">
                <div className={feature.color}>
                  {feature.icon}
                </div>
                <div className="pb-chatbot-feature-card-text">
                  <h4 className="pb-chatbot-feature-card-title">{feature.title}</h4>
                  <p className="pb-chatbot-feature-card-desc">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const useConversations = (initialConversations) => {
  const [conversations, setConversations] = useState(() => {
    if (initialConversations && initialConversations.length > 0) {
      return initialConversations;
    }
    const savedConversations = localStorage.getItem('conversations');
    return savedConversations ? JSON.parse(savedConversations) : [
      {
        name: 'New Script Generation',
        messages: [],
      }
    ];
  });

  useEffect(() => {
    localStorage.setItem('conversations', JSON.stringify(conversations));
  }, [conversations]);

  return [conversations, setConversations];
};

const ChatBot = ({
  initialConversations = [],
  initialSelectedConversationIndex = 0,
  projectId
}) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useConversations(initialConversations);
  const [selectedConversationIndex, setSelectedConversationIndex] = useState(initialSelectedConversationIndex);
  const [error, setError] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [theme, setTheme] = useState('light');
  const [scriptsGeneratedToday, setScriptsGeneratedToday] = useState(0);
  const [maxDailyScripts, setMaxDailyScripts] = useState(2);
  const [projectInfo, setProjectInfo] = useState({});
  const [sceneBreakdown, setSceneBreakdown] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);

  useEffect(() => {
    fetchGenerationLimit();
  }, []);

  useEffect(() => {
    if (initialConversations.length > 0 && initialConversations[0].conversationId) {
      setConversationId(initialConversations[0].conversationId);
      const firstAIMessage = initialConversations[0].messages.find(m => !m.fromMe);
      if (firstAIMessage && firstAIMessage.sceneBreakdown) {
        setSceneBreakdown(firstAIMessage.sceneBreakdown);
      }
    }
  }, [initialConversations]);

  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!projectId) return;
      try {
        const response = await fetch(`${baseUrl}/getProjectById`, {
          headers: {
            'project_id': projectId,
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setProjectInfo({
            name: data.title || 'Untitled Project',
            description: data.project_desc || 'No description',
            genre: data.genre || 'Not set',
            type: data.project_type || 'Not set',
            created_by: data.creator_id || '',
            created_at: data.creation_date || ''
          });
        }
      } catch (error) {
      }
    };
    fetchProjectInfo();
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchConversationHistory();
    }
  }, [projectId]);

  const fetchConversationHistory = async () => {
    try {
      const response = await fetch(`${baseUrl}/scriptConversation/getConversationHistory`, {
        headers: {
          'project_id': projectId,
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setConversationHistory(data.conversations || []);
      }
    } catch (error) {
    }
  };

  const fetchGenerationLimit = async () => {
    try {
      const response = await fetch(`${baseUrl}/scriptConversation/getGenerationLimit`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScriptsGeneratedToday(data.scripts_generated_today);
        setMaxDailyScripts(data.daily_limit);
      }
    } catch (error) {
    }
  };

  const handleLoadConversation = async (convId) => {
    if (convId === conversationId) return;
    try {
      const response = await fetch(`${baseUrl}/scriptConversation/getConversation`, {
        headers: {
          'conversation_id': convId,
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        }
      });
      if (!response.ok) throw new Error('Failed to load conversation');
      const data = await response.json();
      const loadedConversation = {
        name: data.title || 'Script Generation',
        conversationId: data.id,
        messages: data.messages.map(msg => ({
          text: msg.content,
          fromMe: msg.message_type === 'user',
          timestamp: msg.created_at,
          status: 'read',
          id: msg.id,
          sceneBreakdown: msg.message_type === 'ai' ? {
            genre: msg.genre,
            characters: msg.characters,
            locations: msg.locations,
            num_scenes: msg.num_scenes,
            shooting_days: msg.shooting_days,
            vfx_required: msg.vfx_required,
            complexity_score: msg.complexity_score
          } : null
        }))
      };
      setConversations([loadedConversation]);
      setConversationId(data.id);
      const lastAIMessage = loadedConversation.messages.filter(m => !m.fromMe).pop();
      if (lastAIMessage && lastAIMessage.sceneBreakdown) {
        setSceneBreakdown(lastAIMessage.sceneBreakdown);
      }
    } catch (error) {
      setError('Failed to load conversation. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleStartNewScript = () => {
    const confirmNew = window.confirm(
      'Are you sure you want to start a new script? This will create a new conversation.'
    );
    if (confirmNew) {
      setConversations([{ name: 'New Script Generation', messages: [] }]);
      setConversationId(null);
      setSceneBreakdown(null);
    }
  };

  const getLatestAIMessage = () => {
    if (conversations.length === 0 || selectedConversationIndex >= conversations.length) return null;
    const currentConversation = conversations[selectedConversationIndex];
    if (!currentConversation || !currentConversation.messages) return null;
    const aiMessages = currentConversation.messages.filter(msg => !msg.fromMe);
    return aiMessages.length > 0 ? aiMessages[aiMessages.length - 1] : null;
  };

  const handleDocumentationClick = () => {
    const latestAIMessage = getLatestAIMessage();
    if (!latestAIMessage || !latestAIMessage.text) {
      alert('No script generated yet! Please generate a script first before using Documentation.');
      return;
    }
    navigate('/script-editor', {
      state: {
        scriptContent: latestAIMessage.text,
        scriptId: latestAIMessage.id || `script_${Date.now()}`,
        conversationId: conversationId,
        messageId: latestAIMessage.id,
        projectId: projectId
      }
    });
  };

  const handleScriptBreakdownClick = () => {
    const latestAIMessage = getLatestAIMessage();
    if (!latestAIMessage || !latestAIMessage.text) {
      alert('No script generated yet! Please generate a script first before using Script Breakdown.');
      return;
    }
    navigate('/script-breakdown', {
      state: {
        scriptContent: latestAIMessage.text,
        scriptId: latestAIMessage.id || `script_${Date.now()}`,
        conversationId: conversationId,
        messageId: latestAIMessage.id,
        projectId: projectId,
        sceneBreakdown: sceneBreakdown
      }
    });
  };

  const handleSendMessage = async (message) => {
    if (message.trim() === '') {
      setError('Message cannot be empty');
      return;
    }

    const isImprovement = message.toLowerCase().includes('improve') ||
      message.toLowerCase().includes('enhance') ||
      message.toLowerCase().includes('better') ||
      message.toLowerCase().includes('refine');

    if (scriptsGeneratedToday >= maxDailyScripts && !isImprovement) {
      setError('Daily limit reached! You can only improve existing scripts today.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    setError('');

    setConversations((prevConversations) => {
      const updatedConversations = [...prevConversations];
      updatedConversations[selectedConversationIndex].messages.push({
        text: message,
        fromMe: true,
        timestamp: new Date().toISOString(),
        status: 'sent',
      });
      return updatedConversations;
    });

    setIsTyping(true);

    try {
      // ── 150s timeout — chunked AI generation can take up to 90s ──────────
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 150000);

      const response = await fetch(`${baseUrl}/scriptConversation/generateScript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        },
        body: JSON.stringify({
          project_id: parseInt(projectId),
          prompt: message,
          genre: projectInfo.genre || '',
          project_type: projectInfo.type === 'Short Film' ? 'short_film' : 'long_film',
          conversation_id: conversationId || null
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errData = {};
        try { errData = await response.json(); } catch {}

        if (response.status === 504 || errData.code === 'TIMEOUT') {
          throw new Error(
            "⏱️ AI took too long generating this script. " +
            "Try a shorter prompt or choose Advertisement type."
          );
        }
        if (response.status === 503 || errData.code === 'GENERATION_FAILED') {
          throw new Error("❌ " + (errData.error || "Script generation failed. Please retry."));
        }
        throw new Error(errData.error || `Server error ${response.status}`);
      }

      const result = await response.json();

      if (!conversationId) {
        setConversationId(result.conversation_id);
        await fetchConversationHistory();
      }

      setConversations((prevConversations) => {
        const updatedConversations = [...prevConversations];
        updatedConversations[selectedConversationIndex].messages.push({
          text: result.script_text,
          fromMe: false,
          timestamp: new Date().toISOString(),
          status: 'read',
          id: result.message_id,
          sceneBreakdown: {
            genre: result.genre,
            characters: result.characters,
            locations: result.locations,
            num_scenes: result.num_scenes,
            shooting_days: result.shooting_days,
            vfx_required: result.vfx_required,
            complexity_score: result.complexity_score
          }
        });
        return updatedConversations;
      });

      setSceneBreakdown({
        genre: result.genre,
        characters: result.characters,
        locations: result.locations,
        num_scenes: result.num_scenes,
        shooting_days: result.shooting_days,
        vfx_required: result.vfx_required,
        complexity_score: result.complexity_score
      });

      if (!isImprovement) {
        setScriptsGeneratedToday(prev => prev + 1);
      }

      await fetchGenerationLimit();

    } catch (error) {
      // Friendly message for abort (timeout)
      const errorMessage = error.name === 'AbortError'
        ? 'Service timeout — the script is still generating. Please wait a moment and refresh.'
        : error.message;

      setConversations((prevConversations) => {
        const updatedConversations = [...prevConversations];
        updatedConversations[selectedConversationIndex].messages.push({
          text: `Error: ${errorMessage}. Please try again.`,
          fromMe: false,
          timestamp: new Date().toISOString(),
          status: 'error',
          id: `error_${Date.now()}`,
        });
        return updatedConversations;
      });

      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsTyping(false);
    }
  };

  const handleEditMessage = async (messageIndex, newText) => {
    const message = conversations[selectedConversationIndex].messages[messageIndex];
    if (message.fromMe || !message.id) return;
    try {
      const response = await fetch(`${baseUrl}/scriptConversation/editScript`, {
        method: 'PUT',
        headers: {
          'message_id': message.id,
          'edited_content': newText,
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        }
      });
      if (!response.ok) throw new Error('Failed to save edit');
      setConversations((prevConversations) => {
        const updatedConversations = [...prevConversations];
        updatedConversations[selectedConversationIndex].messages[messageIndex].text = newText;
        updatedConversations[selectedConversationIndex].messages[messageIndex].edited = true;
        return updatedConversations;
      });
    } catch (error) {
      setError('Failed to save edit. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const cycleTheme = () => {
    setTheme(current => {
      if (current === 'light') return 'dark';
      if (current === 'dark') return 'read';
      return 'light';
    });
  };

  const getThemeIcon = () => {
    if (theme === 'light') return <FiSun className="pb-chatbot-theme-icon" />;
    if (theme === 'dark') return <FiMoon className="pb-chatbot-theme-icon" />;
    return <FiBookOpen className="pb-chatbot-theme-icon" />;
  };

  const getThemeLabel = () => {
    if (theme === 'light') return 'Light Mode';
    if (theme === 'dark') return 'Dark Mode';
    return 'Read Mode';
  };

  return (
    <>
      <SEO
        title="AI Film Assistant"
        description="Chat with ProducerBuddy's AI assistant for instant answers on film production, budgeting, casting, distribution, and more."
        url="https://producerbuddy.pilomation.com/chatbot"
        robots="noindex, nofollow"
      />
      <style>{`
        .pb-chatbot-container {
          display: flex;
          height: 100vh;
          width: 100%;
          overflow: hidden;
          position: relative;
        }
        .pb-chatbot-theme-light { background-color: #ffffff; color: #111827; }
        .pb-chatbot-theme-dark { background-color: #1f2937; color: #f9fafb; }
        .pb-chatbot-theme-read { background-color: #fef3c7; color: #78350f; }
        .pb-chatbot-camera-reel { width: 64px; height: 100%; flex-shrink: 0; position: relative; overflow: hidden; }
        .pb-chatbot-camera-reel-svg { position: absolute; inset: 0; height: 100%; width: 100%; }
        .pb-chatbot-camera-gradient-top { position: absolute; top: 0; width: 100%; height: 64px; background: linear-gradient(to bottom, #000000, transparent); z-index: 10; }
        .pb-chatbot-camera-gradient-bottom { position: absolute; bottom: 0; width: 100%; height: 64px; background: linear-gradient(to top, #000000, transparent); z-index: 10; }
        .pb-chatbot-feature-panel { width: 320px; flex-shrink: 0; display: flex; flex-direction: column; height: 100%; overflow-y: auto; box-shadow: 2px 0 10px rgba(0,0,0,0.1); }
        .pb-chatbot-theme-light.pb-chatbot-feature-panel { background-color: #ffffff; border-right: 1px solid #e5e7eb; }
        .pb-chatbot-theme-dark.pb-chatbot-feature-panel { background-color: #111827; border-right: 1px solid #374151; }
        .pb-chatbot-theme-read.pb-chatbot-feature-panel { background-color: #fef3c7; border-right: 1px solid #fcd34d; }
        .pb-chatbot-feature-header { padding: 24px; border-bottom: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-feature-header { border-color: #e5e7eb; }
        .pb-chatbot-theme-dark .pb-chatbot-feature-header { border-color: #374151; }
        .pb-chatbot-theme-read .pb-chatbot-feature-header { border-color: #fcd34d; }
        .pb-chatbot-feature-header-content { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .pb-chatbot-feature-header-icon { font-size: 32px; color: #14b8a6; }
        .pb-chatbot-feature-header-title { font-size: 24px; font-weight: 700; margin: 0; }
        .pb-chatbot-feature-header-subtitle { font-size: 14px; opacity: 0.75; margin: 0; }
        .pb-chatbot-daily-limit-card { margin: 16px; padding: 16px; border-radius: 12px; border: 2px solid; }
        .pb-chatbot-theme-light .pb-chatbot-daily-limit-card { background-color: #f0fdfa; border-color: #99f6e4; }
        .pb-chatbot-theme-dark .pb-chatbot-daily-limit-card { background-color: #1f2937; border-color: #374151; }
        .pb-chatbot-theme-read .pb-chatbot-daily-limit-card { background-color: #fef3c7; border-color: #fcd34d; }
        .pb-chatbot-daily-limit-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
        .pb-chatbot-daily-limit-label { font-size: 14px; font-weight: 600; }
        .pb-chatbot-daily-limit-badge { font-size: 12px; padding: 4px 8px; border-radius: 9999px; }
        .pb-chatbot-limit-ok { background-color: #d1fae5; color: #065f46; }
        .pb-chatbot-limit-reached { background-color: #fee2e2; color: #991b1b; }
        .pb-chatbot-daily-limit-progress-bg { width: 100%; background-color: #d1d5db; border-radius: 9999px; height: 10px; overflow: hidden; }
        .pb-chatbot-daily-limit-progress-bar { height: 100%; border-radius: 9999px; transition: width 0.5s ease; }
        .pb-chatbot-progress-teal { background-color: #14b8a6; }
        .pb-chatbot-progress-red { background-color: #ef4444; }
        .pb-chatbot-daily-limit-warning { font-size: 12px; color: #dc2626; font-weight: 500; margin-top: 8px; margin-bottom: 0; }
        .pb-chatbot-scene-breakdown-card { margin: 0 16px 16px; padding: 16px; border-radius: 12px; border: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-scene-breakdown-card { background-color: #f0f9ff; border-color: #bae6fd; }
        .pb-chatbot-theme-dark .pb-chatbot-scene-breakdown-card { background-color: #1e293b; border-color: #334155; }
        .pb-chatbot-theme-read .pb-chatbot-scene-breakdown-card { background-color: #fef3c7; border-color: #fcd34d; }
        .pb-chatbot-scene-breakdown-title { font-size: 14px; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; color: #6366f1; }
        .pb-chatbot-scene-breakdown-content { font-size: 13px; }
        .pb-chatbot-scene-stat { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; padding: 6px 0; border-bottom: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-scene-stat { border-color: #e0f2fe; }
        .pb-chatbot-theme-dark .pb-chatbot-scene-stat { border-color: #334155; }
        .pb-chatbot-theme-read .pb-chatbot-scene-stat { border-color: #fde68a; }
        .pb-chatbot-scene-label { font-weight: 600; color: #64748b; }
        .pb-chatbot-scene-value { font-weight: 700; color: #6366f1; }
        .pb-chatbot-scene-list { margin-top: 12px; padding-top: 12px; border-top: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-scene-list { border-color: #e0f2fe; }
        .pb-chatbot-theme-dark .pb-chatbot-scene-list { border-color: #334155; }
        .pb-chatbot-theme-read .pb-chatbot-scene-list { border-color: #fde68a; }
        .pb-chatbot-scene-items { list-style: none; padding: 0; margin: 8px 0 0 0; }
        .pb-chatbot-scene-items li { padding: 4px 0; font-size: 12px; color: #64748b; }
        .pb-chatbot-project-info-card { margin: 0 16px 16px; padding: 16px; border-radius: 12px; border: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-project-info-card { border-color: #e5e7eb; background-color: #f9fafb; }
        .pb-chatbot-theme-dark .pb-chatbot-project-info-card { border-color: #374151; background-color: #1f2937; }
        .pb-chatbot-theme-read .pb-chatbot-project-info-card { border-color: #fcd34d; background-color: #fef3c7; }
        .pb-chatbot-project-info-title { font-size: 14px; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
        .pb-chatbot-project-info-content { font-size: 12px; }
        .pb-chatbot-project-detail { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 12px; }
        .pb-chatbot-project-detail-icon { font-size: 16px; margin-top: 2px; flex-shrink: 0; color: #14b8a6; }
        .pb-chatbot-project-label { font-weight: 600; display: block; margin-bottom: 2px; color: #64748b; }
        .pb-chatbot-project-value { margin: 0; font-size: 13px; line-height: 1.4; }
        .pb-chatbot-project-lock-notice { font-size: 11px; opacity: 0.75; margin-top: 12px; padding-top: 12px; border-top: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-project-lock-notice { border-color: #e5e7eb; }
        .pb-chatbot-theme-dark .pb-chatbot-project-lock-notice { border-color: #374151; }
        .pb-chatbot-theme-read .pb-chatbot-project-lock-notice { border-color: #fcd34d; }
        .pb-chatbot-conversation-history-card { margin: 0 16px 16px; padding: 16px; border-radius: 12px; border: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-conversation-history-card { background-color: #fef3c7; border-color: #fde68a; }
        .pb-chatbot-theme-dark .pb-chatbot-conversation-history-card { background-color: #1e293b; border-color: #334155; }
        .pb-chatbot-theme-read .pb-chatbot-conversation-history-card { background-color: #fef3c7; border-color: #fcd34d; }
        .pb-chatbot-conversation-history-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .pb-chatbot-conversation-history-title { font-size: 14px; font-weight: 600; display: flex; align-items: center; gap: 8px; color: #f59e0b; margin: 0; }
        .pb-chatbot-conversation-list { display: flex; flex-direction: column; gap: 8px; max-height: 300px; overflow-y: auto; }
        .pb-chatbot-conversation-item { padding: 12px; border-radius: 8px; cursor: pointer; transition: all 0.2s; border: 1px solid; }
        .pb-chatbot-theme-light .pb-chatbot-conversation-item { background-color: white; border-color: #e5e7eb; }
        .pb-chatbot-theme-light .pb-chatbot-conversation-item:hover { background-color: #f9fafb; transform: translateX(4px); box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .pb-chatbot-theme-dark .pb-chatbot-conversation-item { background-color: #374151; border-color: #4b5563; }
        .pb-chatbot-theme-dark .pb-chatbot-conversation-item:hover { background-color: #4b5563; transform: translateX(4px); box-shadow: 0 2px 4px rgba(0,0,0,0.3); }
        .pb-chatbot-theme-read .pb-chatbot-conversation-item { background-color: #fef3c7; border-color: #fcd34d; }
        .pb-chatbot-theme-read .pb-chatbot-conversation-item:hover { background-color: #fde68a; transform: translateX(4px); }
        .pb-chatbot-conversation-active { border-color: #14b8a6 !important; box-shadow: 0 0 0 2px rgba(20,184,166,0.2) !important; }
        .pb-chatbot-conversation-item-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
        .pb-chatbot-conversation-name { font-size: 13px; font-weight: 600; }
        .pb-chatbot-conversation-active-badge { font-size: 10px; padding: 2px 6px; border-radius: 4px; background-color: #14b8a6; color: white; font-weight: 700; }
        .pb-chatbot-conversation-date { font-size: 11px; display: flex; align-items: center; gap: 4px; }
        .pb-chatbot-theme-light .pb-chatbot-conversation-date { color: #6b7280; }
        .pb-chatbot-theme-dark .pb-chatbot-conversation-date { color: #9ca3af; }
        .pb-chatbot-theme-read .pb-chatbot-conversation-date { color: #92400e; }
        .pb-chatbot-conversation-date-icon { font-size: 10px; }
        .pb-chatbot-features-section { padding: 16px; flex: 1; }
        .pb-chatbot-features-title { font-size: 18px; font-weight: 600; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
        .pb-chatbot-features-title-icon { font-size: 20px; }
        .pb-chatbot-features-list { display: flex; flex-direction: column; gap: 12px; }
        .pb-chatbot-feature-card { padding: 16px; border-radius: 8px; border: 1px solid; cursor: pointer; transition: all 0.2s; }
        .pb-chatbot-theme-light .pb-chatbot-feature-card { border-color: #e5e7eb; }
        .pb-chatbot-theme-light .pb-chatbot-feature-card:hover { background-color: #f9fafb; box-shadow: 0 4px 6px rgba(0,0,0,0.1); transform: translateY(-2px); }
        .pb-chatbot-theme-dark .pb-chatbot-feature-card { border-color: #374151; }
        .pb-chatbot-theme-dark .pb-chatbot-feature-card:hover { background-color: #1f2937; box-shadow: 0 4px 6px rgba(0,0,0,0.3); transform: translateY(-2px); }
        .pb-chatbot-theme-read .pb-chatbot-feature-card { border-color: #fcd34d; }
        .pb-chatbot-theme-read .pb-chatbot-feature-card:hover { background-color: #fef3c7; box-shadow: 0 4px 6px rgba(0,0,0,0.1); transform: translateY(-2px); }
        .pb-chatbot-feature-card-content { display: flex; align-items: flex-start; gap: 12px; }
        .pb-chatbot-feature-icon { font-size: 24px; }
        .pb-chatbot-color-yellow { color: #f59e0b; }
        .pb-chatbot-color-blue { color: #3b82f6; }
        .pb-chatbot-color-purple { color: #a855f7; }
        .pb-chatbot-color-green { color: #10b981; }
        .pb-chatbot-color-indigo { color: #6366f1; }
        .pb-chatbot-color-rose { color: #f43f5e; }
        .pb-chatbot-feature-card-text { flex: 1; }
        .pb-chatbot-feature-card-title { font-size: 14px; font-weight: 600; margin: 0 0 4px 0; }
        .pb-chatbot-feature-card-desc { font-size: 12px; opacity: 0.75; margin: 0; }
        .pb-chatbot-main-content { flex: 1; display: flex; flex-direction: column; height: 100%; overflow: hidden; min-width: 0; }
        .pb-chatbot-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; border-bottom: 1px solid; flex-shrink: 0; }
        .pb-chatbot-theme-light .pb-chatbot-header { background-color: #ffffff; border-color: #e5e7eb; }
        .pb-chatbot-theme-dark .pb-chatbot-header { background-color: #1f2937; border-color: #374151; }
        .pb-chatbot-theme-read .pb-chatbot-header { background-color: #fef3c7; border-color: #fcd34d; }
        .pb-chatbot-header-title { font-size: 24px; font-weight: 700; margin: 0 0 4px 0; }
        .pb-chatbot-header-subtitle { font-size: 14px; opacity: 0.75; margin: 0; }
        .pb-chatbot-header-actions { display: flex; align-items: center; gap: 12px; }
        .pb-chatbot-new-script-btn { display: flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; transition: all 0.2s; font-size: 14px; font-weight: 600; background-color: #f59e0b; color: white; }
        .pb-chatbot-new-script-btn:hover { background-color: #d97706; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0,0,0,0.15); }
        .pb-chatbot-theme-button { display: flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; transition: all 0.2s; font-size: 14px; font-weight: 500; }
        .pb-chatbot-theme-light .pb-chatbot-theme-button { background-color: #f3f4f6; color: #111827; }
        .pb-chatbot-theme-light .pb-chatbot-theme-button:hover { background-color: #e5e7eb; }
        .pb-chatbot-theme-dark .pb-chatbot-theme-button { background-color: #374151; color: #f9fafb; }
        .pb-chatbot-theme-dark .pb-chatbot-theme-button:hover { background-color: #4b5563; }
        .pb-chatbot-theme-read .pb-chatbot-theme-button { background-color: #fcd34d; color: #78350f; }
        .pb-chatbot-theme-read .pb-chatbot-theme-button:hover { background-color: #fbbf24; }
        .pb-chatbot-theme-icon { font-size: 20px; }
        .pb-chatbot-content-area { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-height: 0; }
        .pb-chatbot-empty-state { display: flex; align-items: center; justify-content: center; height: 100%; }
        .pb-chatbot-empty-state-content { text-align: center; }
        .pb-chatbot-empty-state-icon { font-size: 64px; margin: 0 auto 16px; opacity: 0.5; }
        .pb-chatbot-empty-state-text { font-size: 20px; opacity: 0.75; }
        .pb-chatbot-error-toast { position: fixed; bottom: 120px; left: 50%; transform: translateX(-50%); background-color: #ef4444; color: white; padding: 12px 24px; border-radius: 8px; box-shadow: 0 10px 25px rgba(0,0,0,0.3); z-index: 9999; animation: pb-chatbot-bounce 0.5s; }
        @keyframes pb-chatbot-bounce { 0%, 100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-10px); } }
      `}</style>

      <div className={`pb-chatbot-container pb-chatbot-theme-${theme}`}>
        <CameraReel side="left" theme={theme} />

        <FeaturePanel
          theme={theme}
          scriptsGeneratedToday={scriptsGeneratedToday}
          maxDailyScripts={maxDailyScripts}
          projectInfo={projectInfo}
          onFeatureClick={handleDocumentationClick}
          onScriptBreakdownClick={handleScriptBreakdownClick}
          sceneBreakdown={sceneBreakdown}
          conversationHistory={conversationHistory}
          currentConversationId={conversationId}
          onLoadConversation={handleLoadConversation}
          onNewScript={handleStartNewScript}
        />

        <div className="pb-chatbot-main-content">
          <div className="pb-chatbot-header">
            <div>
              <h1 className="pb-chatbot-header-title">Script Generator</h1>
              <p className="pb-chatbot-header-subtitle">AI-Powered Scriptwriting Assistant</p>
            </div>
            <div className="pb-chatbot-header-actions">
              <button onClick={cycleTheme} className="pb-chatbot-theme-button" title="Change theme">
                {getThemeIcon()}
                <span>{getThemeLabel()}</span>
              </button>
            </div>
          </div>

          <div className="pb-chatbot-content-area">
            {conversations.length > 0 ? (
              <>
                <ChatWindow
                  conversation={conversations[selectedConversationIndex]}
                  onEditMessage={handleEditMessage}
                  isTyping={isTyping}
                  theme={theme}
                />
                <MessageInput
                  onSendMessage={handleSendMessage}
                  setIsTyping={setIsTyping}
                  theme={theme}
                  disabled={scriptsGeneratedToday >= maxDailyScripts}
                />
              </>
            ) : (
              <div className="pb-chatbot-empty-state">
                <div className="pb-chatbot-empty-state-content">
                  <MdAutoAwesome className="pb-chatbot-empty-state-icon" />
                  <p className="pb-chatbot-empty-state-text">No conversation selected</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <CameraReel side="right" theme={theme} />
      </div>

      {error && (
        <div className="pb-chatbot-error-toast">
          {error}
        </div>
      )}
    </>
  );
};

ChatBot.propTypes = {
  initialConversations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      conversationId: PropTypes.number,
      messages: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          fromMe: PropTypes.bool.isRequired,
          timestamp: PropTypes.string.isRequired,
          status: PropTypes.string.isRequired,
          sceneBreakdown: PropTypes.object
        })
      ).isRequired,
    })
  ),
  initialSelectedConversationIndex: PropTypes.number,
  projectId: PropTypes.string
};

CameraReel.propTypes = {
  side: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired
};

FeaturePanel.propTypes = {
  theme: PropTypes.string.isRequired,
  scriptsGeneratedToday: PropTypes.number.isRequired,
  maxDailyScripts: PropTypes.number.isRequired,
  projectInfo: PropTypes.object,
  onFeatureClick: PropTypes.func.isRequired,
  onScriptBreakdownClick: PropTypes.func,
  sceneBreakdown: PropTypes.object,
  conversationHistory: PropTypes.array,
  currentConversationId: PropTypes.number,
  onLoadConversation: PropTypes.func,
  onNewScript: PropTypes.func
};

export default ChatBot;
